/**
 * What one run says about itself, in four cells.
 *
 * **These live apart from the panel that lists them because the sentences are
 * the deliverable.** `ReportRunsPanel` imports the API client, which imports
 * `lib/config.ts`, which reads `import.meta.env` and therefore only evaluates
 * inside Vite — so a panel that owned this markup could not be rendered by
 * `scripts/verify-report-screens.tsx`, and the copy would be checked by nobody.
 * Slice 12 made the same split for the same reason and said so; this is that
 * precedent applied to the screen where the lie would be about a file.
 *
 * **The lie this file exists to prevent is "no report".** A run that has not
 * started, a run that is building and a run that failed are three different
 * facts and get three different sentences, and none of them is a blank cell, a
 * dash or a greyed-out download button:
 *
 *   - `queued` — nothing has gone wrong. It is waiting.
 *   - `running` — nothing has gone wrong either, and the copy promises no
 *     duration: a 366-day window does not finish in "a few seconds".
 *   - `failed` — **this is the one that must survive every future edit.** It is
 *     labelled Failed, in the failure tone, with the contract's own sentence for
 *     its code beside it and the code itself printed for a support conversation.
 *     Rendered as an absent report it would be the same defect as a 0% bounce
 *     rate on a workspace that never sent: the difference between "nothing
 *     happened" and "we could not find out what happened", which is the entire
 *     point of `failed` being a status rather than a missing row.
 *
 * **Every run states the period it covered, first.** "Generated 3 March" tells
 * nobody what is in the file; the window does, and it is printed with
 * `describeReportWindow` — which spells out "up to but not including" rather
 * than trusting a bracket — in the zone the window was chosen in. This is the
 * screen half of decision 4: the sheet inside carries the same statement,
 * because a spreadsheet outlives the screen it came from.
 *
 * **A download button exists only where a file does.** `reportRunArtifact` is
 * the only way this file asks — a guard, so the branch that has no file has to
 * be written rather than defaulting to `undefined` and rendering an empty
 * button. And the button is only ever offered for a `succeeded` run, whose
 * artifact the contract makes non-optional; a succeeded run with no file is
 * unrepresentable, so there is no fourth case hiding here.
 *
 * `describeReportTrigger` can return a real email address. It is rendered here
 * — this is a workspace member reading their own workspace's run record, which
 * is exactly where the contract says that string belongs, alongside the sheet —
 * and it must not travel anywhere else from here. It is never logged.
 */
import {
  REPORT_RUN_STATUS_LABELS,
  REPORT_TITLES,
  describeReportTrigger,
  describeReportWindow,
  isReportRunFailed,
  reportRunArtifact,
  reportRunDurationMs,
  reportRunStatusDetail,
  type ReportRun,
} from '@wend/shared';
import { Badge, Button, formatFileSize } from '@wend/ui';
import { Download } from 'lucide-react';

import { formatDateTime } from '../../lib/format';
import {
  REPORT_RUN_TONE,
  formatDurationMs,
  reportWindowLengthLabel,
} from '../../lib/report-display';

/**
 * The period, in the zone it was chosen in — the first thing on the row.
 *
 * The report's own title sits above it rather than the enum member: a row headed
 * `credit_usage` was named by the schema, not for the person reading it.
 */
export function ReportRunPeriodCell({ run }: { run: ReportRun }) {
  const { window, kind } = run.definition;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[0.9rem] leading-snug text-text">{REPORT_TITLES[kind]}</span>
      <span className="text-[0.82rem] leading-snug text-muted">{describeReportWindow(window)}</span>
      <span className="font-mono text-[0.66rem] uppercase tracking-[0.1em] text-muted">
        {reportWindowLengthLabel(window)}
      </span>
    </div>
  );
}

/**
 * The status, its sentence, and — on a failure — the code to quote.
 *
 * The label and the sentence both come from `@wend/shared`: a second wording of
 * "this took too long, try a shorter period" written here would be the version
 * that stops matching the reason the API actually recorded.
 */
export function ReportRunStatusCell({ run }: { run: ReportRun }) {
  const detail = reportRunStatusDetail(run);

  return (
    <div className="flex flex-col gap-1.5">
      <span>
        {/* Slice 66: the status pill carries the Blueprint dot, in the badge's own
            colour and aria-hidden — a shape to find down the column, never a
            second carrier of the status, which is still the word. */}
        <Badge tone={REPORT_RUN_TONE[run.status]} dot>
          {REPORT_RUN_STATUS_LABELS[run.status]}
        </Badge>
      </span>
      {detail !== '' && (
        <span className="max-w-prose text-[0.82rem] leading-snug text-muted">{detail}</span>
      )}
      {isReportRunFailed(run) && (
        <span className="font-mono text-[0.66rem] uppercase tracking-[0.1em] text-muted">
          {run.failure.code}
        </span>
      )}
    </div>
  );
}

/**
 * When it was asked for, by whom or by what, and how long it took.
 *
 * "Started by" is on every row rather than only on scheduled ones, because a
 * blank there reads as a field that failed to load — and once an unattended run
 * exists, the answer to "who asked for this" is the whole reason the run record
 * has a trigger at all.
 *
 * **The timestamp here is in the reader's own zone, and the period next to it is
 * in the window's** — deliberately, and they are told apart by the period naming
 * its zone while this does not. "When did I ask for this" is a question about
 * the reader's day, and every other screen in this app answers it the same way;
 * "what does the file cover" is a question about the workspace's chosen clock,
 * which is why the sheet inside prints the generation time in the window's zone
 * rather than in anybody's local one.
 */
export function ReportRunProvenanceCell({ run }: { run: ReportRun }) {
  const duration = reportRunDurationMs(run);

  return (
    <div className="flex flex-col gap-1">
      <span className="whitespace-nowrap text-[0.82rem] text-muted">
        {formatDateTime(run.requestedAt)}
      </span>
      <span className="break-words text-[0.78rem] leading-snug text-muted">
        Started by {describeReportTrigger(run.trigger)}
      </span>
      {duration !== null && (
        <span className="font-mono text-[0.66rem] uppercase tracking-[0.1em] tabular-nums text-muted">
          took {formatDurationMs(duration)}
        </span>
      )}
    </div>
  );
}

export interface ReportRunFileCellProps {
  run: ReportRun;
  /** Called with the run that has a file. Never called for a run without one. */
  onDownload: (run: ReportRun) => void;
  /** True while this run's bytes are being fetched. */
  downloading: boolean;
}

/**
 * The file, or the reason there is not one.
 *
 * There is no state in which this renders nothing. "No file yet" and "No file
 * was produced" are different statements, and the second one is deliberately not
 * a repeat of the failure sentence in the status cell — that cell says *why*
 * this run failed; this one says what that means for the download somebody came
 * here looking for.
 */
export function ReportRunFileCell({ run, onDownload, downloading }: ReportRunFileCellProps) {
  const artifact = reportRunArtifact(run);

  if (artifact === null) {
    return (
      <span className="text-[0.82rem] leading-snug text-muted">
        {isReportRunFailed(run) ? 'No file was produced.' : 'No file yet.'}
      </span>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1.5">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={downloading}
        leadingIcon={<Download aria-hidden="true" className="size-4" />}
        onClick={() => {
          onDownload(run);
        }}
      >
        {downloading ? 'Fetching' : 'Download'}
      </Button>
      <span className="break-all font-mono text-[0.66rem] tracking-[0.04em] text-muted">
        {artifact.fileName}
      </span>
      <span className="font-mono text-[0.66rem] uppercase tracking-[0.1em] tabular-nums text-muted">
        {formatFileSize(artifact.byteSize)}
      </span>
    </div>
  );
}
